import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useHistory } from 'react-router-dom';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import http from '../../services/http';
import { toast } from 'react-toastify';

const PERIODS = [
  { value: 'day', label: 'Diária' },
  { value: 'week', label: 'Semanal' },
  { value: 'month', label: 'Mensal' },
];

// Frase da janela do período, para textos.
const WINDOW_PHRASE = { day: 'hoje', week: 'nesta semana', month: 'neste mês' };

const GROUPS = [
  { value: 'assignee', label: 'Responsável' },
  // Departamento = grupo de atendimento DO RESPONSÁVEL (item 9). Fica ao lado
  // de "Responsável" de propósito: é a mesma pergunta num nível acima.
  { value: 'service_group', label: 'Departamento' },
  { value: 'service', label: 'Serviço' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Prioridade' },
];

const PendingDashboardPage = () => {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const [period, setPeriod] = useState('month');
  const [groupBy, setGroupBy] = useState('assignee');
  const [data, setData] = useState({ total: 0, items: [], series: [] });
  const [loading, setLoading] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);

  // Drill-down (item 23): abre a lista de tickets que compõem uma barra, para
  // conferir os números. Usa o mecanismo ticketPanel que a página de Tickets já
  // entende (filtra pelos IDs recebidos via state).
  const openTicketsForIds = (label, ids) => {
    const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (list.length === 0) {
      toast.info('Sem tickets para exibir neste grupo.');
      return;
    }
    history.push('/ticket', { ticketPanel: { label: `Pendências · ${label}`, ids: list } });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/indicators/pending', { params: { group_by: groupBy, period } });
      const d = res.data || {};
      setData({
        total: d.total || 0,
        items: Array.isArray(d.items) ? d.items : [],
        series: Array.isArray(d.series) ? d.series : [],
      });
    } catch (e) {
      toast.error('Erro ao carregar pendências.');
      setData({ total: 0, items: [], series: [] });
    } finally {
      setLoading(false);
    }
  }, [groupBy, period]);

  useEffect(() => { load(); }, [load]);

  const maxCount = useMemo(
    () => data.items.reduce((m, it) => Math.max(m, it.count || 0), 0),
    [data.items]
  );

  const maxSeries = useMemo(
    () => data.series.reduce((m, it) => Math.max(m, it.count || 0), 0),
    [data.series]
  );

  const groupLabel = GROUPS.find((g) => g.value === groupBy)?.label || 'Grupo';

  const buildExportRows = () =>
    data.items.map((it) => ({ [groupLabel]: it.label, Pendências: it.count }));

  const handleExport = (format) => {
    setExportAnchor(null);
    if (!data.items.length) {
      toast.info('Nada para exportar.');
      return;
    }
    const rows = buildExportRows();
    const stamp = new Date().toISOString().slice(0, 10);
    const base = `pendencias_${groupBy}_${period}_${stamp}`;
    if (format === 'csv') {
      const headers = Object.keys(rows[0]);
      const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers.map(escape).join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${base}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pendências');
      XLSX.writeFile(wb, `${base}.xlsx`);
    }
    toast.success(`${rows.length} linha(s) exportada(s).`);
  };

  const barColor = theme.palette.primary.main;

  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto', backgroundColor: theme.palette.background.default }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            Dashboard de Pendências
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Pendências abertas {WINDOW_PHRASE[period] || ''} — por {groupLabel.toLowerCase()}.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ textTransform: 'none' }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={(e) => setExportAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>
            Exportar
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('xlsx')}>Exportar XLSX</MenuItem>
            <MenuItem onClick={() => handleExport('csv')}>Exportar CSV</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: theme.palette.text.secondary }}>Período</Typography>
          <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)}>
            {PERIODS.map((p) => (
              <ToggleButton key={p.value} value={p.value} sx={{ textTransform: 'none' }}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: theme.palette.text.secondary }}>Agrupar por</Typography>
          <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, v) => v && setGroupBy(v)}>
            {GROUPS.map((g) => (
              <ToggleButton key={g.value} value={g.value} sx={{ textTransform: 'none' }}>{g.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{data.total}</Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>pendência(s) {WINDOW_PHRASE[period] || ''}</Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : data.items.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4, textAlign: 'center' }}>
              Nenhuma pendência no período selecionado.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {data.items.map((it, idx) => {
                const pct = maxCount > 0 ? Math.max(2, Math.round((it.count / maxCount) * 100)) : 0;
                return (
                  <Tooltip key={`${it.label}-${idx}`} arrow placement="top" title={`Ver os ${it.count} ticket(s) de ${it.label}`}>
                    <Box
                      onClick={() => openTicketsForIds(it.label, it.ids)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer', borderRadius: 1, p: 0.5, mx: -0.5,
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                      }}
                    >
                      <Box sx={{ width: 180, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.label}>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{it.label}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0, backgroundColor: theme.palette.action.hover, borderRadius: 1, height: 24, position: 'relative' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 1, transition: 'width 0.3s' }} />
                      </Box>
                      <Box sx={{ width: 48, flexShrink: 0, textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{it.count}</Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
            Quantidade de pendências por dia
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            Passe o mouse sobre uma coluna para ver o dia e a quantidade.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : data.series.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4, textAlign: 'center' }}>
              Sem dados no período selecionado.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 200, overflowX: 'auto', pb: 1 }}>
              {data.series.map((pt) => {
                const pct = maxSeries > 0 ? Math.max(3, Math.round((pt.count / maxSeries) * 100)) : 0;
                return (
                  <Box key={pt.date} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', minWidth: 28, height: '100%' }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
                      <Tooltip arrow title={`Dia ${pt.label}: ${pt.count} pendência(s) — clique para ver`} placement="top">
                        <Box
                          onClick={() => openTicketsForIds(`dia ${pt.label}`, pt.ids)}
                          sx={{
                            width: 20,
                            height: `${pct}%`,
                            minHeight: 4,
                            backgroundColor: barColor,
                            borderRadius: '4px 4px 0 0',
                            cursor: 'pointer',
                            transition: 'height 0.3s, opacity 0.15s',
                            '&:hover': { opacity: 0.8 },
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography variant="caption" sx={{ mt: 0.5, color: theme.palette.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
                      {pt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PendingDashboardPage;
